'use client';

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, ClipboardCheck, RotateCcw, Sparkles } from 'lucide-react';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import { SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX } from '../viewport';
import { createLogger } from '@/lib/debugLogger';
import { uiViewportHeight, uiViewportWidth } from '@/lib/uiScale';

const logger = createLogger('SmartActionButton');

export const SMART_ACTION_BUTTON_COLOR = '#0f65fa';
const SMART_ACTION_TRIGGER_OPEN_Z_INDEX = SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX + 4;
const SMART_ACTION_MENU_Z_INDEX = SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX + 5;
const SMART_ACTION_PENDING_OVERLAY_Z_INDEX = SMARTTASK_TOPMOST_FULLSCREEN_MODAL_Z_INDEX + 6;

export type SmartActionButtonState = 'default' | 'paused' | 'resumable';
export type SmartActionButtonDisabledReason = 'location' | 'inspection' | 'generic';

type SmartActionTone = 'blue' | 'emerald' | 'cyan' | 'amber' | 'red' | 'slate';

const SMART_ACTION_TRIGGER_STATE_STYLES: Record<SmartActionButtonState, { backgroundColor: string; borderColor: string; indicatorClassName: string }> = {
	default: {
		backgroundColor: SMART_ACTION_BUTTON_COLOR,
		borderColor: SMART_ACTION_BUTTON_COLOR,
		indicatorClassName: 'bg-white/80',
	},
	paused: {
		backgroundColor: '#f59e0b',
		borderColor: '#f59e0b',
		indicatorClassName: 'bg-white',
	},
	resumable: {
		backgroundColor: '#059669',
		borderColor: '#059669',
		indicatorClassName: 'bg-white',
	},
};

const SMART_ACTION_TONE_CLASSNAMES: Record<SmartActionTone, string> = {
	blue: 'bg-blue-600 text-white hover:bg-blue-700',
	emerald: 'bg-emerald-600 text-white hover:bg-emerald-700',
	cyan: 'bg-cyan-600 text-white hover:bg-cyan-700',
	amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30',
	red: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30',
	slate: 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700',
};

export interface SmartActionMenuItem {
	key: string;
	label: string;
	icon?: React.ElementType;
	onClick: () => void | Promise<void>;
	executionMode?: 'immediate' | 'await';
	tone?: SmartActionTone;
	toneClassName?: string;
	requiresLocation?: boolean;
}

interface SmartActionButtonProps {
	items: SmartActionMenuItem[];
	/** When set, clicking the button executes this item directly instead of opening a dropdown. */
	primaryItem?: SmartActionMenuItem;
	open?: boolean;
	label?: string;
	state?: SmartActionButtonState;
	disabled?: boolean;
	disabledReason?: SmartActionButtonDisabledReason;
	compact?: boolean;
	fullWidth?: boolean;
	menuColumns?: 1 | 2;
	menuPlacement?: 'top' | 'bottom';
	buttonClassName?: string;
	menuClassName?: string;
	containerClassName?: string;
	supplementaryContent?: React.ReactNode;
	onOpenChange?: (isOpen: boolean) => void;
	onLocationRequired?: (item: SmartActionMenuItem) => boolean | Promise<boolean>;
	/**
	 * Fired when the button is tapped while disabled because of a location
	 * gate (i.e. `disabled && disabledReason === 'location'`). Typical use is
	 * to deep-link the user to the OS location settings so they can re-enable
	 * GPS and return — at which point the resume handlers in `useGeolocation`
	 * will refresh shared state and re-enable this button.
	 */
	onLocationDisabledTap?: () => void | Promise<void>;
	/**
	 * Fired when the button is tapped while disabled because of an inspection
	 * gate (i.e. `disabled && disabledReason === 'inspection'`). Typical use is
	 * to open the vehicle inspection modal directly so the user can complete
	 * the required inspection.
	 */
	onInspectionDisabledTap?: () => void | Promise<void>;
	onBeforeItemAction?: (item: SmartActionMenuItem) => boolean | Promise<boolean>;
	dataTourId?: string;
	itemDataTourIdPrefix?: string;
	icon?: React.ElementType;
}

export default function SmartActionButton({
	items,
	open,
	label = 'Smart Action',
	state = 'default',
	disabled = false,
	disabledReason,
	compact = false,
	fullWidth = true,
	menuColumns = 1,
	menuPlacement = 'top',
	buttonClassName,
	menuClassName,
	containerClassName,
	supplementaryContent,
	onOpenChange,
	onLocationRequired,
	onLocationDisabledTap,
	onInspectionDisabledTap,
	onBeforeItemAction,
	dataTourId,
	itemDataTourIdPrefix,
	icon: ButtonIcon = Sparkles,
	primaryItem: propPrimaryItem,
}: SmartActionButtonProps) {
	const [internalIsOpen, setInternalIsOpen] = useState(false);
	const [pendingAction, setPendingAction] = useState<{ key: string; label: string } | null>(null);
	const [showPendingOverlay, setShowPendingOverlay] = useState(false);
	const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const pendingOverlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isControlled = typeof open === 'boolean';
	const isOpen = isControlled ? open : internalIsOpen;
	const isBusy = pendingAction !== null;
	const isDisabled = disabled || isBusy;
	const isLocationDisabled = disabled && disabledReason === 'location';
	const isInspectionDisabled = disabled && disabledReason === 'inspection';
	const effectiveItems = useMemo(() => {
		if (state !== 'paused') {
			return items;
		}

		// When paused, only show resume/unpause actions - no photos, comments, or signature
		const resumableItems = items.filter((item) => /resume|unpause/i.test(item.key) || /resume|unpause/i.test(item.label));
		return resumableItems;
	}, [items, state]);
	const pausedPrimaryItem = useMemo(
		() => state === 'paused' && effectiveItems.length === 1 ? effectiveItems[0] : null,
		[effectiveItems, state],
	);
	const directPrimaryItem = propPrimaryItem || pausedPrimaryItem || null;

	const hasOverlayContent = effectiveItems.length > 0 || Boolean(supplementaryContent);
	const hasActiveOverlay = isOpen || Boolean(supplementaryContent);
	const triggerStateStyle = SMART_ACTION_TRIGGER_STATE_STYLES[state];
	const buttonLabel = isLocationDisabled
		? 'GPS Required'
		: isInspectionDisabled
			? 'Inspection Required'
		: state === 'paused'
			? 'Unpause'
			: label;
	const EffectiveButtonIcon = isLocationDisabled
		? AlertTriangle
		: isInspectionDisabled
			? ClipboardCheck
		: state === 'paused'
			? RotateCcw
			: ButtonIcon;
	const resolvedButtonClassName = compact
		? 'inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:opacity-95'
		: 'w-full flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-95';
	const resolvedMenuClassName = useMemo(() => {
		const gridClassName = menuColumns === 2 ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1 gap-2';
		return `rounded-2xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800 ${gridClassName}`;
	}, [menuColumns]);

	const getMenuStyle = useCallback((): React.CSSProperties => {
		if (!menuRect) return { display: 'none' };
		const GAP = 8;
		if (menuPlacement === 'bottom') {
			return {
				position: 'fixed',
				zIndex: SMART_ACTION_MENU_Z_INDEX,
				top: menuRect.bottom + GAP,
				...(fullWidth
					? { left: menuRect.left, width: menuRect.width }
					: { right: uiViewportWidth() - menuRect.right, minWidth: '15rem' }),
			};
		}
		return {
			position: 'fixed',
			zIndex: SMART_ACTION_MENU_Z_INDEX,
			bottom: uiViewportHeight() - menuRect.top + GAP,
			...(fullWidth
				? { left: menuRect.left, width: menuRect.width }
				: { right: uiViewportWidth() - menuRect.right, minWidth: '15rem' }),
		};
	}, [menuRect, menuPlacement, fullWidth]);

	useEffect(() => {
		if (!hasActiveOverlay) {
			return;
		}

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) {
				return;
			}

			if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
				return;
			}

			if (!isControlled) {
				setInternalIsOpen(false);
			}
			onOpenChange?.(false);
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}

			if (!isControlled) {
				setInternalIsOpen(false);
			}
			onOpenChange?.(false);
		};

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [hasActiveOverlay, isControlled, onOpenChange]);

	useEffect(() => {
		if (hasOverlayContent) {
			return;
		}

		if (isOpen) {
			if (!isControlled) {
				setInternalIsOpen(false);
			}
			onOpenChange?.(false);
		}
	}, [hasOverlayContent, isControlled, isOpen, onOpenChange]);

	useEffect(() => {
		if (!disabled || !isOpen) {
			return;
		}

		if (!isControlled) {
			setInternalIsOpen(false);
		}
		onOpenChange?.(false);
	}, [disabled, isControlled, isOpen, onOpenChange]);

	useEffect(() => {
		return () => {
			if (pendingOverlayTimerRef.current) {
				clearTimeout(pendingOverlayTimerRef.current);
			}
		};
	}, []);

	const executeItemAction = useCallback(async (item: SmartActionMenuItem) => {
		if (item.requiresLocation) {
			const locationReady = await Promise.resolve(onLocationRequired?.(item) ?? true);
			if (!locationReady) {
				if (!isControlled) {
					setInternalIsOpen(false);
				}
				onOpenChange?.(false);
				return;
			}
		}

		const shouldProceed = await Promise.resolve(onBeforeItemAction?.(item) ?? true);
		if (!shouldProceed) {
			if (!isControlled) {
				setInternalIsOpen(false);
			}
			onOpenChange?.(false);
			return;
		}

		if (!isControlled) {
			setInternalIsOpen(false);
		}
		onOpenChange?.(false);

		if (item.executionMode !== 'await') {
			void Promise.resolve(item.onClick());
			return;
		}

		setPendingAction({ key: item.key, label: item.label });
		setShowPendingOverlay(false);
		pendingOverlayTimerRef.current = setTimeout(() => {
			setShowPendingOverlay(true);
		}, 150);

		try {
			await Promise.resolve(item.onClick());
		} finally {
			if (pendingOverlayTimerRef.current) {
				clearTimeout(pendingOverlayTimerRef.current);
				pendingOverlayTimerRef.current = null;
			}
			setShowPendingOverlay(false);
			setPendingAction(null);
		}
	}, [isControlled, onBeforeItemAction, onLocationRequired, onOpenChange]);

	const handleItemClick = useCallback(async (item: SmartActionMenuItem, event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		await executeItemAction(item);
	}, [executeItemAction]);

	if (!hasOverlayContent) {
		return null;
	}

	return (
		<div
			ref={rootRef}
			className={`relative ${isOpen ? 'z-[80]' : 'z-0'} ${containerClassName || ''}`}
			style={isOpen ? { zIndex: SMART_ACTION_TRIGGER_OPEN_Z_INDEX } : undefined}
		>
			<button
				ref={buttonRef}
				type="button"
				data-smart-action-state={state}
				data-tour-id={dataTourId}
				// When location-disabled with a tap handler, keep the button
				// interactive so the user can deep-link into OS settings.
				// Same for inspection-disabled with a tap handler — the user
				// should be able to tap to open the inspection modal directly.
				disabled={isDisabled && !(isLocationDisabled && onLocationDisabledTap) && !(isInspectionDisabled && onInspectionDisabledTap)}
				aria-busy={isBusy}
				aria-disabled={isDisabled}
				onClick={(event) => {
					event.stopPropagation();
					if (isLocationDisabled && onLocationDisabledTap) {
						void Promise.resolve(onLocationDisabledTap());
						return;
					}
					if (isInspectionDisabled && onInspectionDisabledTap) {
						logger.debug("[SmartActionButton] Inspection disabled tap — calling onInspectionDisabledTap", { isInspectionDisabled, hasHandler: !!onInspectionDisabledTap });
						void Promise.resolve(onInspectionDisabledTap());
						return;
					}
					if (isDisabled) {
						return;
					}
					if (directPrimaryItem) {
						void executeItemAction(directPrimaryItem);
						return;
					}
					const nextIsOpen = !isOpen;
					if (nextIsOpen && buttonRef.current) {
						setMenuRect(buttonRef.current.getBoundingClientRect());
					}
					if (!isControlled) {
						setInternalIsOpen(nextIsOpen);
					}
					onOpenChange?.(nextIsOpen);
				}}
				className={`${resolvedButtonClassName} ${buttonClassName || ''}`}
				style={{
					backgroundColor: isLocationDisabled ? '#d97706' : isInspectionDisabled ? '#d97706' : triggerStateStyle.backgroundColor,
					borderColor: isLocationDisabled ? '#d97706' : isInspectionDisabled ? '#d97706' : triggerStateStyle.borderColor,
					opacity: isDisabled ? 0.65 : 1,
					cursor: isDisabled
						? ((isLocationDisabled && onLocationDisabledTap) || (isInspectionDisabled && onInspectionDisabledTap) ? 'pointer' : 'not-allowed')
						: undefined,
				}}
				title={isLocationDisabled
					? (onLocationDisabledTap
						? 'Tap to enable location services'
						: 'Enable location services to use Smart Action')
					: isInspectionDisabled ? (onInspectionDisabledTap ? 'Tap to start vehicle inspection' : 'Complete the vehicle inspection before proceeding') : undefined}
			>
				{state !== 'default' && (
					<span
						aria-hidden="true"
						className={`h-2 w-2 flex-shrink-0 rounded-full ${triggerStateStyle.indicatorClassName}`}
					/>
				)}
				<EffectiveButtonIcon className={compact ? 'h-3.5 w-3.5 text-white' : 'h-4 w-4 text-white'} />
				{buttonLabel}
				{!directPrimaryItem && (
					<ChevronDown className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
				)}
			</button>

			{isLocationDisabled && (
				<p className="mt-1 text-center text-[10px] font-medium text-amber-600 dark:text-amber-400">
					Location required
				</p>
			)}
			{isInspectionDisabled && (
				<p className="mt-1 text-center text-[10px] font-medium text-amber-600 dark:text-amber-400">
					Inspection required
				</p>
			)}

			{isOpen && effectiveItems.length > 0 && typeof document !== 'undefined' && createPortal(
			<div ref={menuRef} className={`${resolvedMenuClassName} ${menuClassName || ''}`} style={getMenuStyle()}>
					{effectiveItems.map((item) => {
						const ItemIcon = item.icon || Sparkles;
						const toneClassName = item.toneClassName || SMART_ACTION_TONE_CLASSNAMES[item.tone || 'slate'];

						return (
							<button
								key={item.key}
								type="button"
								data-tour-id={itemDataTourIdPrefix ? `${itemDataTourIdPrefix}-${item.key}` : undefined}
								disabled={isDisabled}
								aria-busy={isBusy && pendingAction?.key === item.key}
								onClick={(event) => {
									void handleItemClick(item, event);
								}}
								className={`flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-colors ${toneClassName}`}
							>
								<ItemIcon className="h-3.5 w-3.5" />
								{item.label}
							</button>
						);
					})}
				</div>,
				document.body,
			)}

			{supplementaryContent}

			{showPendingOverlay && pendingAction && typeof document !== 'undefined' && createPortal(
				<div
					className="fixed inset-0 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
					style={{ zIndex: SMART_ACTION_PENDING_OVERLAY_Z_INDEX }}
				>
					<div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/95 px-4 py-3 text-left shadow-[0_24px_60px_rgba(15,23,42,0.24)] dark:border-white/10 dark:bg-slate-900/95">
						<BrandedLoadingSpinner
							mode="tiny"
							primaryColor={SMART_ACTION_BUTTON_COLOR}
							showLogo
							className="shrink-0"
						/>
						<div className="min-w-0">
							<p className="text-sm font-semibold text-slate-900 dark:text-white">
								{pendingAction.label}
							</p>
							<p className="text-xs text-slate-500 dark:text-slate-300">
								Smart Task is finishing this action…
							</p>
						</div>
					</div>
				</div>,
				document.body,
			)}
		</div>
	);
}
